/**
 * Zodiac tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_meaning`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const zodiacTattoosPack: ToilePack = {
    slug: 'zodiac-tattoos',
    category: 'tattoo',
    name: 'Zodiac tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'constellation', label: 'Constellation', subject: 'the Leo constellation drawn as nine small stars joined by thin straight lines, the brightest star Regulus slightly larger'},
        {key: 'glyph', label: 'Sign glyph', subject: 'the Leo zodiac glyph, a single looped curve, with three tiny stars and a dot scattered around it'},
        {key: 'lion', label: 'Lion and moon', subject: "a lion's head in profile with a full mane, framed inside a thin crescent moon"},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a fine-line tattoo drawing, single-needle hairline strokes of even thin weight, delicate and light, no shading', palette: 'black ink only', framing: 'The design, with every piece clearly separate, centered on the sheet with a wide white margin on every side'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, the form built from fine black dots, dot gradients for shade and no continuous outline', palette: 'black ink only', framing: 'The design, with every piece clearly separate, centered on the sheet with a wide white margin on every side'},
        {key: 'blackwork', label: 'Blackwork', style: 'a blackwork tattoo drawing, solid black silhouettes with sharp edges and a few thin white cut-out lines', palette: 'black ink only', framing: 'The design, with every piece clearly separate, centered on the sheet with a wide white margin on every side'},
    ],
    directions: ['Scorpio', 'red ink', 'forearm'],
    directionHint: 'try: Scorpio',
    seo: {
        title: 'Zodiac tattoos: Leo constellation, glyph, lion',
        metaDescription: 'Nine zodiac tattoo designs: the Leo constellation, the Leo glyph and a lion inside a moon, in three styles.',
        h1: 'Zodiac tattoos: constellation, sign glyph, lion and moon',
        tagline: 'One sign, Leo, shown three ways in fine line, dotwork and blackwork.',
        intro: "People tattoo their star sign as a constellation, a glyph or the animal it stands for. The sheet shows Leo three ways: the constellation as stars joined by fine lines, the Leo glyph with small stars around it, and a lion's head framed by a crescent moon, each in fine line, dotwork and blackwork. Type another sign, such as Scorpio, and the same three forms carry it.",
        useCases: [
            'a star sign tattoo for a birthday',
            'a constellation for the artist to draw from',
            'a sign print for a bedroom wall',
        ],
        faq: [
            {q: 'Which sign is shown?', a: 'Leo is the sample sign, shown as a constellation, a glyph and a lion.'},
            {q: 'Can I get another sign?', a: 'Yes. Type the sign you want and the three forms and three styles are applied to it. The result is a request, not a promised image.'},
            {q: 'What is the difference between the styles?', a: 'Fine line uses thin single strokes, dotwork builds the shape from fine black dots, and blackwork uses solid black shapes with sharp edges.'},
            {q: 'Are these shown on skin?', a: 'No. Each design is shown alone on white paper.'},
        ],
    },
}
